import { Head, Link } from '@inertiajs/react'
import SuperAdminLayout from '../_SuperAdminLayout'
import Filters, { ActivityFilters, OrgOption } from './_Filters'
import { sortPeople } from '../../../utils/peopleSort'

interface GuideRow {
  id: number
  full_name: string
  email: string
  organizations_guided: number
  organization_names: string[]
  last_active_at: string | null
  total_activity: number
  breakdown: {
    sessions: number
    rocks: number
    action_items: number
    topics: number
    meetings: number
    agenda_templates: number
    users: number
    companies: number
    other: number
  }
  company_rollup: {
    meetings_started: number
    active_rocks: number
    active_users: number
  }
  inactive: boolean
}

const CATEGORY_COLUMNS: { key: keyof GuideRow['breakdown']; label: string; tooltip: string }[] = [
  { key: 'sessions', label: 'Sessions', tooltip: 'Logins and page views. Repeat visits to the same screen within 30 minutes count once.' },
  { key: 'rocks', label: 'Rocks', tooltip: 'Rock actions by the guide (created, updated, completed, etc.)' },
  { key: 'action_items', label: 'Action items', tooltip: 'Action item actions by the guide' },
  { key: 'topics', label: 'Topics', tooltip: 'Topic actions by the guide' },
  { key: 'meetings', label: 'Meetings', tooltip: 'Meeting actions by the guide (started, ended, updated, etc.)' },
  { key: 'agenda_templates', label: 'Agenda templates', tooltip: 'Agenda template actions by the guide' },
  { key: 'users', label: 'Users', tooltip: 'User management actions by the guide' },
  { key: 'companies', label: 'Companies', tooltip: 'Company-level actions by the guide' },
  { key: 'other', label: 'Other', tooltip: 'Everything else the guide did — e.g. teams, invitations, charts, metrics, projects, and settings — so the columns always add up to Activity in period.' },
]

const ROLLUP_COLUMNS: { key: keyof GuideRow['company_rollup']; label: string }[] = [
  { key: 'meetings_started', label: 'Co. meetings started' },
  { key: 'active_rocks', label: 'Co. active rocks' },
  { key: 'active_users', label: 'Co. active users' },
]

interface Props {
  guides: GuideRow[]
  filters: ActivityFilters
  organizations: OrgOption[]
}

function formatDate(iso: string | null) {
  if (!iso) return 'Never'
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

const detailQuery = (f: ActivityFilters) => {
  const params = new URLSearchParams()
  params.set('period', f.period)
  if (f.period === 'custom') {
    if (f.from) params.set('from', f.from)
    if (f.to) params.set('to', f.to)
  }
  if (f.organization_id) params.set('organization_id', String(f.organization_id))
  return params.toString()
}

export default function GuideActivityIndex({ guides, filters, organizations }: Props) {
  return (
    <SuperAdminLayout>
      <Head title="SuperAdmin · Guide Activity" />
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-text-primary">Guide Activity</h1>
        <Link href="/super_admin/guides" className="text-sm text-[#ED1C24] hover:underline">
          Manage Guides
        </Link>
      </div>

      <Filters path="/super_admin/guide_activity" filters={filters} organizations={organizations} />

      <div className="bg-white border border-stone/20 rounded-lg">
        {guides.length === 0 ? (
          <div className="py-10 text-center text-text-muted text-sm">No Guides yet.</div>
        ) : (
          <div className="overflow-auto max-h-[70vh] rounded-lg">
            <table className="w-full text-sm border-separate border-spacing-0">
              <thead className="text-text-secondary">
                <tr>
                  <th className="sticky top-0 left-0 z-30 bg-[#F5F5F5] text-left px-4 py-2.5 font-medium min-w-[220px] border-b border-stone/20">
                    Guide
                  </th>
                  <th className="sticky top-0 z-20 bg-[#F5F5F5] text-right px-4 py-2.5 font-medium whitespace-nowrap border-b border-stone/20">Orgs guided</th>
                  <th className="sticky top-0 z-20 bg-[#F5F5F5] text-left px-4 py-2.5 font-medium whitespace-nowrap border-b border-stone/20">Last active</th>
                  <th className="sticky top-0 z-20 bg-[#F5F5F5] text-right px-4 py-2.5 font-medium whitespace-nowrap border-b border-stone/20">Activity in period</th>
                  {CATEGORY_COLUMNS.map(col => (
                    <th
                      key={col.key}
                      title={col.tooltip}
                      className="sticky top-0 z-20 bg-[#F5F5F5] text-right px-4 py-2.5 font-medium whitespace-nowrap border-b border-stone/20 cursor-help"
                    >
                      {col.label}
                    </th>
                  ))}
                  {ROLLUP_COLUMNS.map(col => (
                    <th
                      key={col.key}
                      title="Company-wide count across all companies this guide serves, not the guide's own actions"
                      className="sticky top-0 z-20 bg-[#F5F5F5] text-right px-4 py-2.5 font-medium whitespace-nowrap border-b border-stone/20"
                    >
                      {col.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortPeople(guides).map(g => (
                  <tr key={g.id} className="group">
                    <td className="sticky left-0 z-10 bg-white group-hover:bg-[#FAFAFA] px-4 py-3 min-w-[220px] border-t border-stone/15">
                      <Link
                        href={`/super_admin/guide_activity/${g.id}?${detailQuery(filters)}`}
                        className="font-medium text-text-primary hover:underline"
                      >
                        {g.full_name}
                      </Link>
                      <div className="text-xs text-text-muted">{g.email}</div>
                      {g.inactive && (
                        <span className="inline-block mt-1 text-xs font-semibold bg-[#ED1C24]/10 text-[#ED1C24] px-1.5 py-0.5 rounded-full">
                          No activity in 30 days
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-text-primary bg-white group-hover:bg-[#FAFAFA] border-t border-stone/15" title={g.organization_names.join(', ')}>
                      {g.organizations_guided}
                    </td>
                    <td className="px-4 py-3 text-text-muted whitespace-nowrap bg-white group-hover:bg-[#FAFAFA] border-t border-stone/15">{formatDate(g.last_active_at)}</td>
                    <td className="px-4 py-3 text-right font-semibold text-text-primary bg-white group-hover:bg-[#FAFAFA] border-t border-stone/15">{g.total_activity}</td>
                    {CATEGORY_COLUMNS.map(col => (
                      <td key={col.key} className="px-4 py-3 text-right text-text-muted bg-white group-hover:bg-[#FAFAFA] border-t border-stone/15">
                        {g.breakdown[col.key]}
                      </td>
                    ))}
                    {ROLLUP_COLUMNS.map(col => (
                      <td key={col.key} className="px-4 py-3 text-right text-text-muted bg-white group-hover:bg-[#FAFAFA] border-t border-stone/15">
                        {g.company_rollup[col.key]}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </SuperAdminLayout>
  )
}
